import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, Shield, Cookie, Database, Mail, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import BirdMascot from '../components/BirdMascot';
import Footer from '../components/Footer';

const PrivacyPolicyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = 'December 18, 2025';

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-muted/30 to-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <BirdMascot size={32} />
            <span className="text-xl font-heading font-bold">JailBirds</span>
          </Link>
          
          <Link to="/">
            <Button variant="outline" size="sm" className="gap-2">
              <Home className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="space-y-8"
        >
          {/* Hero Section */}
          <div className="text-center mb-8">
            <Shield className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-4 text-gradient-primary">
              Privacy Policy
            </h1>
            <p className="text-muted-foreground">
              Last Updated: {lastUpdated}
            </p>
          </div>

          {/* Introduction */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <h2 className="text-2xl font-heading font-bold mb-4">Introduction</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                Welcome to JailBirds ("we," "our," or "us"). We are committed to protecting your privacy and ensuring transparency about how we collect, use, and safeguard your information when you visit our website at jailbirdsgame.com (the "Site") and play our game.
              </p>
              <p>
                This Privacy Policy explains our practices regarding information collection and use. By using our Site, you agree to the collection and use of information in accordance with this policy.
              </p>
            </div>
          </div>

          {/* Information We Collect */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <Database className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-heading font-bold">Information We Collect</h2>
            </div>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <h3 className="text-lg font-semibold text-foreground">Information You Provide</h3>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Nickname:</strong> When you play, you may create a nickname to appear on leaderboards</li>
                <li><strong>Avatar Selection:</strong> Your chosen avatar for display purposes</li>
                <li><strong>Game Scores:</strong> Your gameplay results and statistics</li>
              </ul>

              <h3 className="text-lg font-semibold text-foreground mt-6">Information Collected Automatically</h3>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Device Information:</strong> Browser type, operating system, device type</li>
                <li><strong>Usage Data:</strong> Pages visited, time spent, game sessions played</li>
                <li><strong>IP Address:</strong> For security and analytics purposes</li>
                <li><strong>Cookies:</strong> Small data files stored on your device (see Cookie Policy below)</li>
              </ul>
            </div>
          </div>

          {/* Cookies */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <Cookie className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-heading font-bold">Cookie Policy</h2>
            </div>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                We use cookies and similar tracking technologies to enhance your experience on our Site. Cookies are small text files placed on your device that help us:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Remember your preferences and settings</li>
                <li>Understand how you interact with our Site</li>
                <li>Improve our services based on usage patterns</li>
                <li>Provide personalized content and advertisements</li>
              </ul>

              <h3 className="text-lg font-semibold text-foreground mt-6">Types of Cookies We Use</h3>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Essential Cookies:</strong> Required for the Site to function properly</li>
                <li><strong>Analytics Cookies:</strong> Help us understand site usage (Google Analytics)</li>
                <li><strong>Advertising Cookies:</strong> Used to deliver relevant advertisements (Google AdSense)</li>
              </ul>

              <p className="mt-4">
                You can control cookies through your browser settings. However, disabling certain cookies may affect site functionality.
              </p>
            </div>
          </div>

          {/* Third-Party Services */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <Eye className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-heading font-bold">Third-Party Services</h2>
            </div>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>We use the following third-party services that may collect information:</p>
              
              <h3 className="text-lg font-semibold text-foreground mt-4">Google Analytics</h3>
              <p>
                We use Google Analytics to analyze site traffic and usage patterns. Google Analytics uses cookies to collect anonymous information about how visitors use our Site. For more information, visit <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google's Privacy Policy</a>.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4">Google AdSense</h3>
              <p>
                We display advertisements through Google AdSense. Google may use cookies to serve ads based on your prior visits to our Site or other websites. You can opt out of personalized advertising by visiting <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Ads Settings</a>.
              </p>

              <h3 className="text-lg font-semibold text-foreground mt-4">PostHog Analytics</h3>
              <p>
                We use PostHog for product analytics to improve user experience and site functionality.
              </p>
            </div>
          </div>

          {/* How We Use Information */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <h2 className="text-2xl font-heading font-bold mb-4">How We Use Your Information</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>We use the information we collect to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide and maintain our game services</li>
                <li>Display leaderboards and player statistics</li>
                <li>Improve and optimize our Site and game experience</li>
                <li>Analyze usage patterns and trends</li>
                <li>Display relevant advertisements</li>
                <li>Prevent fraud and ensure security</li>
                <li>Comply with legal obligations</li>
              </ul>
            </div>
          </div>

          {/* Data Retention */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <h2 className="text-2xl font-heading font-bold mb-4">Data Retention</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                We retain your information for as long as necessary to provide our services and fulfill the purposes described in this policy. Game scores and leaderboard data are retained indefinitely to maintain historical records. You may request deletion of your data by contacting us.
              </p>
            </div>
          </div>

          {/* Your Rights */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <h2 className="text-2xl font-heading font-bold mb-4">Your Rights</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>Depending on your location, you may have the right to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your information</li>
                <li>Object to processing of your information</li>
                <li>Request data portability</li>
                <li>Opt out of marketing communications</li>
              </ul>
              <p className="mt-4">
                To exercise these rights, please contact us at <a href="mailto:privacy@jailbirdsgame.com" className="text-primary hover:underline">privacy@jailbirdsgame.com</a>.
              </p>
            </div>
          </div>

          {/* Children's Privacy */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <h2 className="text-2xl font-heading font-bold mb-4">Children's Privacy</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                Our Site is not intended for children under 18 years of age. We do not knowingly collect personal information from children under 18. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.
              </p>
            </div>
          </div>

          {/* Security */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <h2 className="text-2xl font-heading font-bold mb-4">Security</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                We implement appropriate technical and organizational measures to protect your information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </div>
          </div>

          {/* Changes to Policy */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <h2 className="text-2xl font-heading font-bold mb-4">Changes to This Policy</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. We encourage you to review this Privacy Policy periodically.
              </p>
            </div>
          </div>

          {/* Contact */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <Mail className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-heading font-bold">Contact Us</h2>
            </div>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>If you have any questions about this Privacy Policy, please contact us:</p>
              <ul className="space-y-2 ml-4">
                <li><strong>Email:</strong> <a href="mailto:privacy@jailbirdsgame.com" className="text-primary hover:underline">privacy@jailbirdsgame.com</a></li>
                <li><strong>Website:</strong> <a href="https://jailbirdsgame.com/contact" className="text-primary hover:underline">jailbirdsgame.com/contact</a></li>
              </ul>
            </div>
          </div>

        </motion.div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;